/**
 * Un rubro del catalogo, con sus productos adentro.
 *
 * Es la pieza donde se hace el trabajo: prender el rubro, correrlo de lugar,
 * publicar los 38 productos de una, y tildar los que van uno por uno.
 *
 * LA MISMA FILA QUE LAS OTRAS LISTAS
 * ----------------------------------
 * Asa para arrastrar, "Se ve" y un menu, como las ofertas y las secciones del
 * home. Antes cada rubro tenia dos flechas y tres botones sueltos ("Ocultar
 * rubro", "Publicar todos", "Ocultar todos"), repetidos en los 21 renglones:
 * la pantalla era una pared de botones. Las acciones en masa van al menu
 * porque se usan una vez por rubro, no cada vez que se entra.
 *
 * LOS PRODUCTOS SE PIDEN AL DESPLEGAR
 * -----------------------------------
 * Traer los 394 al entrar tardaria lo mismo que traerlos por rubro, pero
 * obligaria a esperar antes de ver nada. Desplegado, el pedido queda cacheado.
 */
import { useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ChevronDown,
  ChevronRight,
  EyeOff,
  GripVertical,
  ListChecks,
  MoreHorizontal,
} from "lucide-react";

import { Importe } from "@/components/money/importe";
import { Badge } from "@/components/ui/badge";
import { Cargando } from "@/components/ui/cargando";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { IconButton } from "@/components/ui/icon-button";
import { mostrarError } from "@/lib/errores";
import { srcImagenAdmin } from "@/lib/imagenes";
import { aNumero, type Producto, type RubroPublicacion } from "@/lib/tipos";
import { cn } from "@/lib/utils";

import { BotonFoto } from "./BotonFoto";
import {
  useProductosDeRubro,
  usePublicarProductos,
  useQuitarImagen,
  useSubirImagen,
} from "./consultas";
import {
  detalleDeProductoUsado,
  filtrarProductos,
  hayFiltro,
  rubroDesplegado,
  type FiltroPublicacion,
} from "./logica";

export interface FilaRubroProps {
  rubro: RubroPublicacion;
  filtro: FiltroPublicacion;
  /** Forzado abierto cuando hay un filtro activo: esconder resultados no ayuda. */
  abiertoPorFiltro: boolean;
  /** Con un filtro puesto la lista esta recortada: arrastrar no tiene sentido. */
  ordenable: boolean;
  onVisibilidad: (publicado: boolean) => void;
}

export function FilaRubro({
  rubro,
  filtro,
  abiertoPorFiltro,
  ordenable,
  onVisibilidad,
}: FilaRubroProps): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rubro.id,
    disabled: !ordenable,
  });
  const [abiertoAMano, setAbiertoAMano] = useState(false);
  // Con filtro se piden los productos de todos los rubros para buscar adentro;
  // se DIBUJAN solo los que tienen algo que mostrar (`rubroDesplegado`).
  const buscando = abiertoAMano || abiertoPorFiltro;

  const productos = useProductosDeRubro(rubro.id, buscando);
  const publicar = usePublicarProductos();
  const subirImagen = useSubirImagen();
  const quitarImagen = useQuitarImagen();

  const visibles = filtrarProductos(productos.data?.filas ?? [], filtro);
  const abierto = rubroDesplegado({
    abiertoAMano,
    abiertoPorFiltro,
    cargando: productos.isLoading,
    coincidencias: visibles.length,
  });

  async function publicarTodos(publicado: boolean): Promise<void> {
    try {
      await publicar.mutateAsync({ rubro_id: rubro.id, publicado });
    } catch (error) {
      mostrarError(error);
    }
  }

  async function publicarUno(p: Producto, publicado: boolean): Promise<void> {
    try {
      await publicar.mutateAsync({ ids: [p.id], publicado });
    } catch (error) {
      mostrarError(error);
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        "border-b border-border bg-card last:border-b-0",
        !rubro.publicado && "bg-muted",
        isDragging && "relative z-10 shadow-md",
      )}
    >
      {/* ── La fila del rubro ── */}
      <div className="flex items-center gap-2 px-2 py-1">
        {ordenable ? (
          <IconButton
            icono={GripVertical}
            tamano="sm"
            aria-label={`Mover ${rubro.nombre}`}
            className="shrink-0 cursor-grab touch-none text-muted-foreground active:cursor-grabbing"
            {...attributes}
            {...listeners}
          />
        ) : (
          <span className="size-8 shrink-0" aria-hidden="true" />
        )}

        {/* Boton crudo: es una fila entera que se despliega, con dos renglones
            de texto adentro. `Button` alinea y trunca de otra forma. */}
        <button
          type="button"
          onClick={() => setAbiertoAMano((v) => !v)}
          aria-expanded={abierto}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md px-1 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {abierto ? (
            <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{rubro.nombre}</span>
            <span className="block text-xs text-muted-foreground">
              {rubro.total === 0
                ? "Sin productos"
                : `${rubro.publicados} de ${rubro.total} publicados · ${rubro.con_foto} con foto`}
            </span>
          </span>
        </button>

        {!rubro.publicado ? (
          <Badge variant="secondary" className="shrink-0">
            Oculto
          </Badge>
        ) : null}

        <Checkbox
          label="Se ve"
          checked={rubro.publicado}
          onChange={(e) => onVisibilidad(e.target.checked)}
          className="shrink-0 px-2"
        />

        {rubro.total > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                icono={MoreHorizontal}
                tamano="sm"
                aria-label={`Más acciones de ${rubro.nombre}`}
                className="shrink-0"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={publicar.isPending || rubro.publicados === rubro.total}
                onSelect={() => void publicarTodos(true)}
              >
                <ListChecks className="size-4" aria-hidden="true" />
                Publicar los {rubro.total} productos
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={publicar.isPending || rubro.publicados === 0}
                onSelect={() => void publicarTodos(false)}
              >
                <EyeOff className="size-4" aria-hidden="true" />
                Despublicar todos
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : (
          <span className="size-8 shrink-0" aria-hidden="true" />
        )}
      </div>

      {/* ── Los productos ── */}
      {abierto ? (
        <div className="border-t border-border bg-background py-2 pl-12 pr-3">
          {productos.isLoading ? (
            <Cargando texto="Cargando productos…" />
          ) : visibles.length === 0 ? (
            <p className="py-3 text-sm text-muted-foreground">
              {hayFiltro(filtro)
                ? "Ningún producto de este rubro coincide."
                : "Este rubro no tiene productos activos."}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {visibles.map((p) => {
                const precio = aNumero(p.precio_venta);
                return (
                  <li key={p.id} className="flex items-center gap-3 py-2">
                    <BotonFoto
                      src={srcImagenAdmin(p.imagen_url, p.id)}
                      nombre={p.nombre}
                      onSubir={(formulario) => subirImagen.mutateAsync({ id: p.id, formulario })}
                      onQuitar={() => quitarImagen.mutateAsync(p.id)}
                    />

                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm">{p.nombre}</p>
                      {p.es_usado ? (
                        <p className="truncate text-xs text-muted-foreground">
                          {detalleDeProductoUsado(p, p.codigo_interno)}
                        </p>
                      ) : null}
                      {precio > 0 ? (
                        <Importe monto={precio} moneda={p.precio_moneda} tamano="sm" />
                      ) : (
                        // No se esconde ni se bloquea: el aviso de arriba lo
                        // cuenta y el catalogo no lo muestra hasta que tenga
                        // precio. Esconderlo aca seria no poder encontrarlo.
                        <span className="text-xs text-warning">Sin precio</span>
                      )}
                    </div>

                    <Checkbox
                      label={<span className="sr-only">Publicar {p.nombre}</span>}
                      checked={p.publicado}
                      disabled={publicar.isPending}
                      onChange={(e) => void publicarUno(p, e.target.checked)}
                      className="shrink-0"
                    />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
